import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BlobButton from './BlobButton';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, Send } from 'lucide-react';
import { submitLeadWithGeo } from '../services/apiService';
import { useGeolocation } from '../hooks/useGeolocation';

const Footer = () => {
  const { country, isLoading: geoLoading, error: geoError, retry: retryGeo } = useGeolocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[0-9]{10,}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check geolocation - MANDATORY
    if (geoLoading) {
      alert('Please wait while we detect your location...');
      return;
    }
    
    if (geoError) {
      alert('Unable to detect your location. Please check your internet connection and try again.');
      return;
    }
    
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const result = await submitLeadWithGeo({
          fullName: formData.name,
          email: formData.email,
          phoneNumber: formData.phone,
          message: formData.message
        });

        if (result.success) {
          // Redirect handled by apiService
        } else {
          alert(result.message || 'Failed to send message. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting contact form:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  return (
    <footer className="bg-white">
      {/* Contact Form Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              Ready to Bring Your <span className="text-amber-700">Story to Life?</span>
            </h2>
            <p className="text-xl text-gray-600">
              Get in touch with us today and let's start your publishing journey
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-2xl relative z-10" style={{ backgroundColor: '#ffffff', opacity: 1 }}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Email Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.name}</p>}
                </div>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.email}</p>}
                </div>
              </div>

              {/* Country Field - Auto-detected */}
              <div className="relative">
                <input
                  type="text"
                  name="country"
                  placeholder={geoLoading ? "Detecting country..." : "Country"}
                  value={country}
                  disabled
                  className="w-full bg-gray-100 border-0 border-b-2 border-gray-300 outline-none px-0 py-3 text-gray-700 placeholder:text-gray-500 cursor-not-allowed"
                />
              </div>
              
              {/* Phone with Country Code */}
              <div className="relative">
                <div className="flex gap-3">
                  <select
                    className="bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none py-3 text-gray-700 transition-colors cursor-pointer"
                    defaultValue="+1"
                  >
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+34">🇪🇸 +34</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    className="flex-1 bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.phone}</p>}
              </div>
              
              {/* Project Details */}
              <div className="relative">
                <input
                  type="text"
                  name="message"
                  placeholder="Project Details"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                />
                {errors.message && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.message}</p>}
              </div>
              
              {submitSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                  <p className="font-semibold">✓ Message sent successfully!</p>
                  <p className="text-sm">We will respond within 24 hours.</p>
                </div>
              )}
              
              <BlobButton
                type="submit"
                disabled={isSubmitting}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5 mr-2 inline" />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </BlobButton>
            </form>
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="bg-gray-50">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <img 
                src="/assets/img/Brand/logo-dark.png" 
                alt="Rushmore Publishing" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-gray-600 mb-4 text-sm">
                Your trusted partner in publishing excellence. Bringing stories to life since 2010.
              </p>
              <div className="flex gap-3">
                {/* Facebook */}
                <a href="#" className="w-10 h-10 bg-white shadow-lg hover:shadow-xl hover:bg-[#1877F2] rounded-full flex items-center justify-center transition-all group">
                  <Facebook className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                </a>
                
                {/* Twitter */}
                <a href="#" className="w-10 h-10 bg-white shadow-lg hover:shadow-xl hover:bg-[#1DA1F2] rounded-full flex items-center justify-center transition-all group">
                  <Twitter className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                </a>
                
                {/* Instagram - Using gradient */}
                <a href="#" className="w-10 h-10 bg-white shadow-lg hover:shadow-xl rounded-full flex items-center justify-center transition-all group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] opacity-0 group-hover:opacity-100 transition-opacity rounded-full"></div>
                  <Instagram className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors relative z-10" />
                </a>
                
                {/* LinkedIn */}
                <a href="#" className="w-10 h-10 bg-white shadow-lg hover:shadow-xl hover:bg-[#0A66C2] rounded-full flex items-center justify-center transition-all group">
                  <Linkedin className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                </a>
              </div>
              
              {/* Contact Info */}
              <div className="mt-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Contact Us</h4>
                <div className="space-y-3 text-gray-600 text-sm">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-amber-700 flex-shrink-0 mt-1" />
                    <span>700 Flower St Suite 1000, Los Angeles, CA 90017</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-amber-700 flex-shrink-0" />
                    <span>+1 (866) 978-8498</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-700 flex-shrink-0" />
                    <span>info@rushmorepublishing.com</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4">Services</h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li><Link to="/book-writing" className="hover:text-amber-700 transition-colors">Book Writing</Link></li>
                <li><Link to="/book-publishing" className="hover:text-amber-700 transition-colors">Book Publishing</Link></li>
                <li><Link to="/book-marketing" className="hover:text-amber-700 transition-colors">Book Marketing</Link></li>
                <li><Link to="/audiobook-production" className="hover:text-amber-700 transition-colors">Audiobook</Link></li>
                <li><Link to="/book-editing" className="hover:text-amber-700 transition-colors">Book Editing</Link></li>
                <li><Link to="/book-printing" className="hover:text-amber-700 transition-colors">Book Printing</Link></li>
              </ul>
            </div>

            {/* Other Services */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4">Other Services</h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li><Link to="/book-video-trailer" className="hover:text-amber-700 transition-colors">Book Video Trailer</Link></li>
                <li><Link to="/web-design-seo" className="hover:text-amber-700 transition-colors">Web Design & SEO</Link></li>
                <li><Link to="/illustration-services" className="hover:text-amber-700 transition-colors">Illustration Services</Link></li>
                <li><Link to="/article-services" className="hover:text-amber-700 transition-colors">Article Services</Link></li>
                <li><Link to="/book-design" className="hover:text-amber-700 transition-colors">Book Design Services</Link></li>
                <li><Link to="/blog-writing-services" className="hover:text-amber-700 transition-colors">Blog Writing Services</Link></li>
                <li><Link to="/web-content-writing" className="hover:text-amber-700 transition-colors">Web Content Writing</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4">Company</h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li><Link to="/about-us" className="hover:text-amber-700 transition-colors">About Us</Link></li>
                <li><Link to="/blog" className="hover:text-amber-700 transition-colors">Blog</Link></li>
                <li><Link to="/news-updates" className="hover:text-amber-700 transition-colors">News & Updates</Link></li>
                <li><Link to="/contact-us" className="hover:text-amber-700 transition-colors">Contact Us</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="bg-white border-t-2 border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-gray-600 text-sm">
            <p>&copy; {new Date().getFullYear()} Rushmore Publishing. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="hover:text-amber-700 transition-colors">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="hover:text-amber-700 transition-colors">Terms & Conditions</Link>
              <Link to="/disclaimer-policy" className="hover:text-amber-700 transition-colors">Disclaimer</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;